import { AnimatePresence, motion } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { useIsMiniContentHovered } from '../atoms'
import { MiniControlsButtons } from '../components/mini/nested/controls'
import { MiniFinishedControls } from '../components/mini/nested/finished-controls'
import { MiniMenuButton } from '../components/mini/nested/mini-menu'
import { useIsMiniAnchoredLeft } from '../components/mini/side-context'
import { MINI_REVEAL_TRANSITION, miniPresenceAnchor, miniRevealVariants } from '../components/mini/side-motion'
import { AudioNoteSavingButton } from './audio-note-saving-button'
import { AudioNoteStartingButton } from './audio-note-starting-button'
import { AudioNoteWaveformButton } from './audio-note-waveform-button'
import type { AudioNotePhase } from './use-audio-note-phase'

interface PillAudioNoteControlsProps {
  phase: Exclude<AudioNotePhase, 'idle'>
}

export function PillAudioNoteControls({ phase }: PillAudioNoteControlsProps) {
  const isContentHovered = useIsMiniContentHovered()
  const isAnchoredLeft = useIsMiniAnchoredLeft()
  const directionClassName = !isAnchoredLeft ? 'flex-row-reverse' : undefined
  const phaseVariants = miniRevealVariants(isAnchoredLeft)
  const anchorX = miniPresenceAnchor(isAnchoredLeft)

  let content
  switch (phase) {
    case 'starting':
      content = <AudioNoteStartingButton />
      break
    case 'recording':
      content = (
        <div className={twMerge('flex items-center', directionClassName)}>
          <AudioNoteWaveformButton showElapsed={isContentHovered} />
          <AnimatePresence initial={false} mode="popLayout" anchorX={anchorX}>
            {isContentHovered && (
              <motion.div
                key="recording-actions"
                variants={phaseVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                transition={MINI_REVEAL_TRANSITION}
                className={twMerge('flex items-center gap-0.5', directionClassName)}
              >
                <MiniControlsButtons />
                <MiniMenuButton />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )
      break
    case 'saving':
      content = <AudioNoteSavingButton />
      break
    case 'finished':
      content = (
        <div className={twMerge('flex items-center', directionClassName)}>
          <MiniFinishedControls />
          <MiniMenuButton />
        </div>
      )
      break
  }

  return (
    <div className={twMerge('flex h-8 items-center', directionClassName)}>
      <AnimatePresence initial={false} mode="popLayout" anchorX={anchorX}>
        <motion.div
          key={phase}
          variants={phaseVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          transition={MINI_REVEAL_TRANSITION}
        >
          {content}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
